import { createContext } from 'react'
import type {
  CreatePromotionInput,
  MembershipPricingRow,
} from '../../../../types/loyalty'
import type { WHLocation } from '../../../../types/warehouse'

/**
 * Members of the Membership Pricing provider — exposed to descendants of the
 * Membership Pricing tab.
 *
 * The table is driven by the `/stores/:storeId/promotions` list: one row per
 * promotion record. Display fields (name, address, phone, POS) are looked up
 * in the pantry warehouse map via the first id in `location_ids`. The
 * Enabled toggle mirrors `promotion.status === ACTIVE`; the Discount input
 * is a placeholder until the API exposes a per-row discount value.
 */
export interface MembershipPricingContextValue {
  storeId: string

  // ── Server data ─────────────────────────────────────────────────────────────
  isLoading: boolean
  isError: boolean
  refetch: () => void

  // ── Editable rows (promotion + warehouse-map merged) ────────────────────────
  rows: MembershipPricingRow[]
  setRowEnabled: (promotionId: string, enabled: boolean) => void
  setRowDiscount: (promotionId: string, discountPercent: number) => void

  // ── Dirty / save lifecycle ──────────────────────────────────────────────────
  // Save walks the dirty rows and calls PUT /promotions/:id for each one (no
  // bulk-edit endpoint yet). The Save panel reads `dirtyCount` for the
  // "N change(s)" label and gates the Save button on `isDirty && !isSaving`.
  isDirty: boolean
  dirtyCount: number
  isSaving: boolean
  saveError: string | null
  save: () => Promise<void>
  discard: () => void

  // ── Add Location flow ───────────────────────────────────────────────────────
  // Warehouse list filtered down to entries that don't yet have a promotion
  // in the table. The Add Location dialog uses this to populate its picker.
  eligibleLocations: WHLocation[]
  // Creates a new basic / all-products / registered promotion targeting the
  // selected location. Resolves once the promotions-list cache has been
  // invalidated so the table picks up the new row.
  createLocationPromotion: (input: CreatePromotionInput) => Promise<void>
  isCreatingPromotion: boolean
}

export const MembershipPricingContext = createContext<
  MembershipPricingContextValue | undefined
>(undefined)
